import { Component, CUSTOM_ELEMENTS_SCHEMA, DestroyRef, ElementRef, inject, signal, OnInit, AfterViewInit, OnDestroy, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { AdminuiAutocompleteComponent, AutoCompleteSelectEvent } from '../components/forms/adminui-autocomplete/adminui-autocomplete.component';
import { AdminuiAutocompleteGroupDirective } from '../components/forms/adminui-autocomplete/adminui-autocomplete-group.directive';
import { first, firstValueFrom, map } from 'rxjs';
import '@visma-swno/gaia-chat-ui';
import type { VsnAuth, NavigationMenu, LanguageCode, TreeLeaf, AppModules, FeedbackConfig } from '@visma-swno/vsn-navigation';
import { AuthService } from '../core/auth/auth.service';
import { ProfileService } from '../core/auth/profile.service';
import { PROFILE_ROLE } from '../core/auth/profile-role';
import { Context } from '../core/auth/context';
import { ADMIN_UI_TOKEN_KIND, TOKEN_KIND_KALMAR } from '../core/auth/storage-keys';
import { DomainService } from '../core/domain/domain.service';
import { WindowRef } from '../core/domain/window-ref';
import { I18NService } from '../core/i18n/i18n.service';
import { NavbarService } from './navbar.service';
import { ContextGroup } from './context-group';
import { AUTOCOMPLETE_MAX_LIMIT } from './autocomplete-limit';
import { TOKEN_RESPONSE } from '../core/auth/connect-storage-keys';
import { LatestExportComponent } from '../exports/latest-export.component';
import { LatestExportsService } from '../exports/latest-exports.service';

// Help-centre URL per locale; anything else gets the language-neutral page. A table rather than the
// three-case switch this replaced, which counted towards initVsnNavigation's complexity.
const HELP_URLS: Readonly<Record<string, string>> = {
    'nb-NO': 'https://docs.vismasoftware.no/no/admin/',
    'sv-SE': 'https://docs.vismasoftware.no/sv/admin/',
};
const HELP_URL_DEFAULT = 'https://docs.vismasoftware.no/admin/';

@Component({
    selector: 'adminui-nav-shell',
    imports: [FormsModule, AdminuiAutocompleteComponent, AdminuiAutocompleteGroupDirective, LatestExportComponent],
    templateUrl: './nav-shell.component.html',
    styleUrl: './nav-shell.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class NavShellComponent implements OnInit, AfterViewInit, OnDestroy {

    @ViewChild('gaiaChat') gaiaChat?: ElementRef;

    readonly vsnAuth = signal<VsnAuth | undefined>(undefined);
    readonly vsnTree = signal<NavigationMenu | undefined>(undefined);
    readonly vsnLang = signal<LanguageCode>('en');
    readonly vsnHelpUrl = signal<string | undefined>(undefined);
    readonly vsnLandingPageUrl = signal<string | undefined>(undefined);
    readonly vsnModules = signal<AppModules | undefined>(undefined);
    readonly vsnFeedback = signal<FeedbackConfig | undefined>(undefined);

    readonly message = signal<{ [key: string]: string }>({});
    readonly contextGroups = signal<ContextGroup[]>([]);
    readonly selectedContext = signal<Context | undefined>(undefined);

    readonly profileId = signal('');
    readonly baseUrl = signal('');
    readonly lang = signal('');
    readonly isGaiaChatOpen = signal(false);
    readonly isNotificationPanelOpen = signal(false);
    readonly isNotificationActive = signal(false);

    private hostname = ''
    private readonly closeRequestedHandler = () => { this.isGaiaChatOpen.set(false); };
    private readonly authFailedHandler = (event: Event) => { console.error('Gaia auth failed', event); };
    private requestTokenHandler!: (event: Event) => void;
    private logoutClickHandler!: () => void;
    private menuItemChangedHandler!: (e: Event) => void;
    private navigateHandler!: (e: Event) => void;
    private aiAssistantClickHandler!: () => void;
    private notificationPanelHandler!: () => void;

    private readonly authService = inject(AuthService);
    private readonly profileService = inject(ProfileService);
    private readonly navbarService = inject(NavbarService);
    private readonly domainService = inject(DomainService);
    private readonly windowRef = inject(WindowRef);
    private readonly i18nService = inject(I18NService);
    private readonly router = inject(Router);
    private readonly elementRef = inject(ElementRef);
    private readonly latestExportsService = inject(LatestExportsService);
    private readonly destroyRef = inject(DestroyRef);

    ngOnInit(): void {
        this.hostname = this.windowRef.getHostname();
        this.requestTokenHandler = this.handleTokenRequest.bind(this);
        this.initVsnNavigation();
        this.initGaia();
        this.loadContextGroups();
        this.setupEventListeners();
        this.elementRef.nativeElement.addEventListener('close-requested', this.closeRequestedHandler);
        this.elementRef.nativeElement.addEventListener('request-token', this.requestTokenHandler);
        this.handleNotificationPanel();
    }

    ngAfterViewInit(): void {
        if (this.gaiaChat) {
            this.gaiaChat.nativeElement.authStrategy = 'visma';
            this.gaiaChat.nativeElement.tokenProvider = () => this.authService.getAccessToken();
            this.gaiaChat.nativeElement.addEventListener('auth-failed', this.authFailedHandler);
        }
    }

    ngOnDestroy(): void {
        this.elementRef.nativeElement.removeEventListener('close-requested', this.closeRequestedHandler);
        this.elementRef.nativeElement.removeEventListener('request-token', this.requestTokenHandler);
        const hostEl = this.elementRef.nativeElement as HTMLElement;
        hostEl.removeEventListener('vsn-logout-click', this.logoutClickHandler);
        hostEl.removeEventListener('vsn-menu-item-changed', this.menuItemChangedHandler);
        hostEl.removeEventListener('vsn-navigate', this.navigateHandler);
        hostEl.removeEventListener('vsn-ai-assistant-click', this.aiAssistantClickHandler);
        if (this.gaiaChat) {
            this.gaiaChat.nativeElement.removeEventListener('auth-failed', this.authFailedHandler);
        }
        hostEl.removeEventListener('vsn-notification-click', this.notificationPanelHandler);
    }

    onSearchContext(event: any): void {
        const page = 1;
        const limit = AUTOCOMPLETE_MAX_LIMIT;
        if (event.originalEvent.type === 'click') {
            this.authService.getContextResponse(page, limit).pipe(first()).subscribe(data => {
                this.contextGroups.set(this.navbarService.getContextGroups(data.contexts, this.message()));
            });
        } else {
            this.authService.getContextResponse(page, limit, event.query).pipe(first()).subscribe(data => {
                this.contextGroups.set(data ? this.navbarService.getContextGroups(data.contexts, this.message()) : []);
            });
        }
    }

    onSelectContext(event: AutoCompleteSelectEvent): void {
        const context: Context = event.value;
        if (this.profileService.profile.currentContext.contextId !== context.contextId) {
            this.profileService.profile.currentContext = context;
            this.authService.setCurrentContextOnStorage(context);
            if (context.roles?.includes(PROFILE_ROLE.administrator)) {
                // Kalmar (non-tenant) context: switch to a Kalmar token. No AMA patch (endpoint is AMA-only);
                // the context is persisted in session storage above.
                sessionStorage.setItem(ADMIN_UI_TOKEN_KIND, TOKEN_KIND_KALMAR);
                this.windowRef.setLocationHref(this.authService.getConnectLoginUrl(this.hostname, { kalmarToken: true }));
            } else {
                // Tenant (AMA) context: the tenant callback persists lastUsedTenantId once the tenant token is issued.
                sessionStorage.removeItem(ADMIN_UI_TOKEN_KIND);
                this.windowRef.setLocationHref(this.authService.getConnectLoginUrl(this.hostname));
            }
        }
    }

    onFocus(event: Event): void {
        (event.currentTarget as HTMLInputElement).select();
    }

    onBlurContext(): void {
        this.selectedContext.set(structuredClone(this.profileService.profile.currentContext));
    }

    /**
     * Which node the navbar tree highlights: the leaf the browser is actually on, and only failing
     * that the one the signed-in roles land on. The URL has to win, or a screen entered by reload or
     * bookmark - which the shell now keeps instead of redirecting - is highlighted as user-access and
     * gets the wrong breadcrumb.
     *
     * The role fallback was lifted out of the tree literal: it was a three-level nested ternary there
     * (S3358), and reading it meant untangling the negation in the first test from the other two.
     */
    private currentTreeItem(roles: string[], intermediateId: string, treeLeaves: TreeLeaf[]): string {
        const pathname = this.windowRef.getPathname();
        const leafOnScreen = treeLeaves.find(leaf => leaf.url === pathname);
        if (leafOnScreen) {
            return leafOnScreen.id;
        }

        const subscriberViewerOnly = roles.includes(PROFILE_ROLE.buSubscriberViewer)
            && !roles.includes(PROFILE_ROLE.buAccessAdministrator);
        if (subscriberViewerOnly) {
            return 'subscribers';
        }
        return roles.includes(PROFILE_ROLE.administrator) ? intermediateId : 'user-access';
    }

    /**
     * The middle level of the navbar tree - which section the signed-in roles land in, and the leaves
     * under it.
     *
     * Lifted out of initVsnNavigation, which measured a complexity of 22 with this role chain inline.
     * The order of the branches is load-bearing: a BU role wins over subscriber, which wins over
     * company, which wins over administrator.
     */
    private buildTreeSection(
        message: { [key: string]: string },
        rootPath: string,
        labels: { userAccessLabel: string; customersLabel: string; onPremisesIntegrationsLabel: string }
    ): { intermediateId: string; intermediateLabel: string; treeLeaves: TreeLeaf[] } {
        const profile = this.profileService.profile;
        const roles = profile.roles;
        const treeLeaves: TreeLeaf[] = [];
        const userAccessLeaf: TreeLeaf = { id: 'user-access', label: labels.userAccessLabel, url: rootPath + '/user-access' };

        const isBuRole = roles.includes(PROFILE_ROLE.buAccessAdministrator) || roles.includes(PROFILE_ROLE.buSubscriberViewer);
        if (isBuRole) {
            if (roles.includes(PROFILE_ROLE.buAccessAdministrator)) {
                treeLeaves.push(userAccessLeaf);
            }
            if (roles.includes(PROFILE_ROLE.buSubscriberViewer)) {
                treeLeaves.push({ id: 'subscribers', label: labels.customersLabel, url: rootPath + '/subscribers/user-access' });
            }
            return { intermediateId: 'business-unit', intermediateLabel: message['labelBusinessUnit'], treeLeaves };
        }

        if (roles.includes(PROFILE_ROLE.subscriberAdministrator)) {
            treeLeaves.push(userAccessLeaf);
            if (this.profileService.onPremiseIntegration) {
                treeLeaves.push({ id: 'onPremisesIntegrations', label: labels.onPremisesIntegrationsLabel, url: rootPath + '/on-premises-integrations' });
            }
            return { intermediateId: 'subscriber', intermediateLabel: message['labelSubscriber'], treeLeaves };
        }

        if (roles.includes(PROFILE_ROLE.companyAdministrator)) {
            treeLeaves.push(userAccessLeaf);
            return { intermediateId: 'company', intermediateLabel: message['labelCompany'], treeLeaves };
        }

        if (roles.includes(PROFILE_ROLE.administrator)) {
            return profile.currentContext.taskId
                ? { intermediateId: 'set_up_your_company', intermediateLabel: message['labelSetUpYourCompany'], treeLeaves }
                : { intermediateId: 'provisioning', intermediateLabel: message['labelProvisioning'], treeLeaves };
        }

        // No recognised role: the caller previously read `intermediateId` as an unassigned
        // definite-assignment field here, i.e. undefined at runtime. Kept as empty strings so the
        // shape is honest rather than lying about being a string.
        return { intermediateId: '', intermediateLabel: '', treeLeaves };
    }

    private initVsnNavigation(): void {
        const profile = this.profileService.profile;
        const navbarMsg: { [key: string]: string } = this.i18nService.messages['navbar'] || {};

        const message = {
            ...this.i18nService.messages['navbar'],
            ...this.i18nService.messages['help'],
            ...this.i18nService.messages['common']
        };
        this.message.set(message);

        const langCode = profile.locale.split('-')[0];
        const supportedLangs: LanguageCode[] = ['en', 'no', 'nb', 'nn', 'sv', 'nl', 'fi', 'da'];
        this.vsnLang.set(supportedLangs.includes(langCode as LanguageCode) ? langCode as LanguageCode : 'en');

        this.vsnAuth.set({
            user: {
                userId: profile.sub,
                userName: profile.name,
                userEmail: profile.email,
            },
            profilePictureBaseUrl: this.domainService.getVsnProfilePictureBaseUrl(this.hostname),
            myProfileUrl: this.domainService.getVsnMyProfileUrl(this.hostname),
        });

        this.vsnLandingPageUrl.set(this.domainService.getVsnLandingPageUrl(this.hostname));

        this.vsnHelpUrl.set(HELP_URLS[profile.locale] ?? HELP_URL_DEFAULT);

        const roles = profile.roles;
        const rootPath = this.navbarService.getRootContextPath(roles, profile.currentContext.taskId);
        const userAccessLabel = navbarMsg['menuItemUserAccess'] || 'User access';
        const customersLabel = navbarMsg['menuItemCustomers'] || 'Customers';
        const onPremisesIntegrationsLabel = navbarMsg['menuItemOnPremisesIntegration'] || 'On-premises integration';

        const { intermediateId, intermediateLabel, treeLeaves } = this.buildTreeSection(
            message, rootPath,
            { userAccessLabel, customersLabel, onPremisesIntegrationsLabel }
        );

        const currentAppId = 'Access_Management';
        const currentAppName = 'Admin';

        this.vsnTree.set({
            items: [{
                id: currentAppId,
                label: currentAppName,
                items: [{
                    id: intermediateId,
                    label: intermediateLabel,
                    ...(treeLeaves.length > 0
                        ? { items: treeLeaves as [TreeLeaf, ...TreeLeaf[]] }
                        : { url: rootPath })
                }]
            }],
            currentItem: this.currentTreeItem(roles, intermediateId, treeLeaves),
            search: {
                enabled: true
            }
        });

        this.vsnModules.set({
            activeModuleId: currentAppId,
            activeModuleLabel: currentAppName,
            modules: () => firstValueFrom(
                this.navbarService.getApplicationsVersion2().pipe(
                    map(apps =>
                        apps.map(a => ({
                            // Kept falsy-checking: an application with an empty id must fall back to
                            // its name, otherwise the nav item gets an empty id and cannot be targeted.
                            // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                            id: a.id || a.name.toLowerCase(),  // NOSONAR falsy check: an empty id must fall back to the name
                            label: a.name,
                            url: a.url
                        }))
                    )
                )
            )
        });

        this.vsnFeedback.set(this.domainService.getVsnFeedbackConfig(this.hostname));

        this.selectedContext.set(structuredClone(profile.currentContext));
    }

    private initGaia(): void {
        const profile = this.profileService.profile;
        this.profileId.set('admin-prod');
        this.baseUrl.set(this.domainService.getGaiaChatApiUrl(this.hostname));
        this.lang.set(profile.locale.split('-')[0]);
    }

    private loadContextGroups(): void {
        this.authService.getContextResponse(1, AUTOCOMPLETE_MAX_LIMIT).pipe(first()).subscribe(data => {
            this.contextGroups.set(this.navbarService.getContextGroups(data.contexts, this.message()));
        });
    }

    private setupEventListeners(): void {
        const hostEl = this.elementRef.nativeElement as HTMLElement;

        this.logoutClickHandler = () => { this.router.navigate(['/logout']); };
        this.menuItemChangedHandler = (e: Event) => {
            const leaf = (e as CustomEvent<TreeLeaf>).detail;
            this.router.navigateByUrl(leaf.url);
            const tree = this.vsnTree();
            if (tree) {
                this.vsnTree.set({ ...tree, currentItem: leaf.id });
            }
        };
        this.navigateHandler = (e: Event) => {
            const item = (e as CustomEvent<{ url: string }>).detail;
            this.windowRef.setLocationHref(item.url);
        };
        this.aiAssistantClickHandler = () => {
            this.isNotificationPanelOpen.set(false);
            this.isGaiaChatOpen.update(open => !open);
        };
        this.notificationPanelHandler = () => {
            this.isGaiaChatOpen.set(false);
            this.isNotificationPanelOpen.update(open => !open);
            if (this.isNotificationPanelOpen()) {
                this.latestExportsService.loadLatestExports();
            }
        };

        hostEl.addEventListener('vsn-logout-click', this.logoutClickHandler);
        hostEl.addEventListener('vsn-menu-item-changed', this.menuItemChangedHandler);
        hostEl.addEventListener('vsn-navigate', this.navigateHandler);
        hostEl.addEventListener('vsn-ai-assistant-click', this.aiAssistantClickHandler);
        hostEl.addEventListener('vsn-notification-click', this.notificationPanelHandler);
    }

    private handleTokenRequest(event: Event): void {
        const customEvent = event as CustomEvent;
        const { callback } = customEvent.detail;
        const tokenStr = sessionStorage.getItem(TOKEN_RESPONSE);
        if (tokenStr) {
            try {
                const token = JSON.parse(tokenStr);
                callback(token.access_token);
            } catch (e) {
                console.error('Failed to parse token from session storage', e);
                callback(undefined);
            }
        } else {
            callback(undefined);
        }
    }

    private handleNotificationPanel(): void {
        this.latestExportsService.openLatestExportsPanel$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(value => {
                if (value) {
                    this.isGaiaChatOpen.set(false);
                    this.isNotificationPanelOpen.set(true);
                    this.latestExportsService.loadLatestExports();
                } else {
                    this.isNotificationPanelOpen.set(false);
                }
            });

        this.latestExportsService.latestExports$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(latestExports => {
                this.isNotificationActive.set(latestExports.length > 0);
            });

        this.latestExportsService.loadLatestExports();
    }
}
